import { useState, useEffect, useCallback } from "react";
import { ArrowRight, Loader2, LogOut, PenLine, BookOpen, LayoutDashboard, Compass, LogIn, Eye, EyeOff, Users, PlayCircle } from "lucide-react";
import { loginAccount, registerAccount, fetchMe, forgotPassword, cJoinCourse, AUTH_TOKEN_KEY } from "@/lib/api";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

const TERRA = "#8C3A2A";
const USER_KEY = "compass_auth_user";

// Production home ("/"). ONE Sign in, gated by the EXISTING JWT auth (no new auth system); after signing in you land by ROLE:
// student → the pathway chooser (Full Writing Studio / Strengthen My Writing) · teacher → the Teacher Dashboard (/teacher) · admin → the dashboard's Admin tab (/admin).
// A remembered sign-in skips the home page. "/student" (`initialPortal="student"`) is the student experience for anyone — including
// educators using "Student view" — and never redirects an educator away. ONE ACCOUNT, SEVERAL ROLES: "Create your student account" with an
// email that already has an account signs that account in (its own password) and adds student access — the person then enters the student
// pathway (student intent), keeping every role they had. `?join=CODE` (a teacher's invitation link) enters the code for them after sign-in.
export const landingFor = (u) => (u && u.role === "admin" ? "/admin" : u && u.role === "teacher" ? "/teacher" : null);   // null = the student pathway, here
export const hasStudentAccess = (u) => !!u && (u.roles || [u.role]).some((r) => r === "student" || r === "user");

export default function PortalHome({ initialPortal = null, initialJoin = "" }) {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);
  const [view, setView] = useState(initialPortal === "student" ? "auth" : "home");   // home | auth | auth-register | student-pathway
  const [joinCode, setJoinCode] = useState(initialJoin || "");
  const [joined, setJoined] = useState(null);                                         // { title } after a join link / code succeeded
  const studentOnly = initialPortal === "student";
  const go = (href) => { window.location.href = href; };
  const joinWith = useCallback(async (code) => {                                      // student-controlled enrolment by code (ordinary join path)
    const r = await cJoinCourse(code.trim().toUpperCase());
    setJoined(r.course); setJoinCode("");
    return r.course;
  }, []);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      let me = null;
      if (token) {
        try { me = await fetchMe(); setUser(me); localStorage.setItem(USER_KEY, JSON.stringify(me)); }
        catch { localStorage.removeItem(AUTH_TOKEN_KEY); localStorage.removeItem(USER_KEY); }
      }
      if (me) {
        const dest = landingFor(me);
        if (dest && !studentOnly) { go(dest); return; }                  // remembered educator sign-in → straight to the dashboard
        if (initialJoin) { try { await joinWith(initialJoin); } catch { /* shown in the chooser */ } }
        setView("student-pathway");
      }
      setBooting(false);
    })();
  }, [studentOnly, initialJoin, joinWith]);

  const onAuthed = async (u, mode, as = "") => {
    if (u && u.email_verified === false) { window.location.assign("/"); return; }   // → App shows the confirm-your-email screen
    setUser(u);
    // WHERE TO LAND: the context the person chose at sign-in decides ("Sign in as" — student · teacher · administrator · individual writer);
    // the student door (register, /student, a join link) is student intent; with no choice, the account's primary role decides.
    const studentIntent = studentOnly || mode === "register" || !!initialJoin || as === "student" || as === "user";
    const dest = as === "teacher" ? "/teacher" : as === "admin" ? "/admin" : landingFor(u);
    if (dest && !studentIntent) { go(dest); return; }
    if (initialJoin && hasStudentAccess(u)) { try { await joinWith(initialJoin); } catch { /* shown in the chooser */ } }
    setView("student-pathway");
  };

  const signOut = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY); localStorage.removeItem(USER_KEY);
    try { localStorage.removeItem("compass_student_session"); localStorage.removeItem("compass_ot_session"); } catch (e) { /* ignore */ }
    setUser(null);
    if (studentOnly) { window.location.assign("/"); return; }   // signing out always lands on the home page, never on a sign-in form
    setView("home");
  };

  const goHome = () => { if (studentOnly) go("/"); else setView(user ? "student-pathway" : "home"); };

  if (booting) return <div className="min-h-screen grid place-items-center bg-[#faf9f6]"><Loader2 className="h-6 w-6 animate-spin text-[#8C3A2A]" /></div>;

  return (
    <div data-testid="portal-home" className="min-h-screen bg-[#faf9f6] text-stone-900 flex flex-col">
      <header className="w-full border-b border-stone-200/70">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button data-testid="portal-wordmark" onClick={goHome} className="flex items-center gap-2 group">
            <span className="grid place-items-center h-7 w-7 rounded-sm" style={{ background: TERRA }}><PenLine className="h-4 w-4 text-white" /></span>
            <span className="font-serif text-[17px] text-stone-900 group-hover:text-[#8C3A2A] transition-colors">The Writing Studio</span>
          </button>
          {user ? (
            <div className="flex items-center gap-3">
              <span data-testid="portal-current-user" className="text-[12px] text-stone-500">{user.name || user.email} · {(user.roles || [user.role]).join(" + ")}</span>
              {landingFor(user) ? <a data-testid="portal-teacher-dashboard" href={landingFor(user)} className="inline-flex items-center gap-1 text-[12px] text-[#8C3A2A] hover:underline"><LayoutDashboard className="h-3.5 w-3.5" /> {user.role === "admin" ? "Admin dashboard" : "Teacher dashboard"}</a> : null}
              <button data-testid="portal-signout" onClick={signOut} className="inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-[#8C3A2A]"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
            </div>
          ) : view === "home" ? (
            <button data-testid="portal-header-signin" onClick={() => setView("auth")} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#8C3A2A] hover:underline"><LogIn className="h-4 w-4" /> Sign in</button>
          ) : null}
        </div>
      </header>

      <main className="flex-1 w-full max-w-5xl mx-auto px-6 py-12 sm:py-16">
        {view === "home" && (
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#8C3A2A] mb-3">The Writing Studio</p>
            <h1 className="font-serif text-4xl sm:text-5xl leading-tight text-stone-900 max-w-2xl">AI for developing thinking.<br /><span className="text-[#8C3A2A]">Not replacing it.</span></h1>
            <p className="text-stone-600 mt-4 text-[16px] max-w-xl">One sign-in for everyone. Choose whether you're here as a student, teacher, administrator or individual writer, and you'll land in the right place.</p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button data-testid="portal-signin" onClick={() => setView("auth")}
                className="inline-flex items-center gap-2 bg-[#8C3A2A] text-white text-[16px] font-medium px-7 py-3.5 rounded-md hover:bg-[#6B2C20] transition-colors">
                <LogIn className="h-5 w-5" /> Sign in
              </button>
              <button data-testid="portal-create-account" onClick={() => setView("auth-register")} className="text-[14px] text-stone-600 hover:text-[#8C3A2A] underline underline-offset-4 decoration-stone-300">
                New here? Create an account
              </button>
            </div>
            <p className="text-[13px] text-stone-500 mt-4 max-w-xl">Teachers join through the invitation link from their administrator; students join a course with the code their teacher shares.</p>
            <a data-testid="portal-how-it-works-card" href="/how-it-works"
              className="mt-5 block rounded-2xl border border-stone-200 bg-white/60 p-5 hover:border-[#8C3A2A]/40 hover:bg-white transition-colors group">
              <div className="flex items-center gap-3">
                <PlayCircle className="h-6 w-6 text-[#8C3A2A] shrink-0" />
                <div className="min-w-0">
                  <h2 className="font-serif text-xl text-stone-900">The Full Studio: How It Works</h2>
                  <p className="text-[13.5px] text-stone-600 mt-0.5">A two-minute preview of one essay from beginning to end — thesis, essay, TWS, Big Picture, Strengthen.</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#8C3A2A] ml-auto shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </a>
            <a data-testid="portal-how-strengthen-card" href="/how-strengthen-works"
              className="mt-5 block rounded-2xl border border-stone-200 bg-white/60 p-5 hover:border-[#8C3A2A]/40 hover:bg-white transition-colors group">
              <div className="flex items-center gap-3">
                <PlayCircle className="h-6 w-6 text-[#8C3A2A] shrink-0" />
                <div className="min-w-0">
                  <h2 className="font-serif text-xl text-stone-900">The &lsquo;Strengthen My Writing&rsquo; Option: How It Works</h2>
                  <p className="text-[13.5px] text-stone-600 mt-0.5">A two-minute walkthrough for writing you already have — type it in, the whole, the paragraphs, the sentences, the final read, finished.</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#8C3A2A] ml-auto shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </a>
            {/* Third entry point — the pre-existing guided Experience / Teacher Review pathway (?experience). */}
            <a data-testid="portal-experience-card" href="/?experience"
              className="mt-5 block rounded-2xl border border-stone-200 bg-white/60 p-5 hover:border-[#8C3A2A]/40 hover:bg-white transition-colors group">
              <div className="flex items-center gap-3">
                <Compass className="h-6 w-6 text-[#8C3A2A] shrink-0" />
                <div className="min-w-0">
                  <h2 className="font-serif text-xl text-stone-900">Experience The Writing Studio</h2>
                  <p className="text-[13.5px] text-stone-600 mt-0.5">A guided walkthrough, including Teacher Review — sign in or create a free account to begin.</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#8C3A2A] ml-auto shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </a>
            <p data-testid="portal-walkthrough-links" className="mt-5 text-[13px] text-stone-600">
              Short walkthroughs:{" "}
              <a data-testid="portal-students-start-link" href="/how-students-start" className="text-[#8C3A2A] underline decoration-stone-300 underline-offset-4 hover:decoration-[#8C3A2A]">New students — how to begin</a>
              <span className="text-stone-300 mx-2">·</span>
              <a data-testid="portal-teachers-link" href="/how-teachers-work" className="text-[#8C3A2A] underline decoration-stone-300 underline-offset-4 hover:decoration-[#8C3A2A]">Teachers — the dashboard</a>
            </p>
          </div>
        )}

        {(view === "auth" || view === "auth-register") && (
          <AuthPanel startMode={view === "auth-register" ? "register" : "login"} onAuthed={onAuthed} onBack={goHome} joinCode={initialJoin} />
        )}

        {view === "student-pathway" && (
          <PathwayChooser onBack={user ? null : goHome} onFull={() => go("/?studio=full")} onComposition={() => go("/?studio=composition")}
            joinCode={joinCode} setJoinCode={setJoinCode} onJoin={joinWith} joined={joined} onClasses={() => go("/?classroom=student")} />
        )}

      </main>
    </div>
  );
}

function PathwayChooser({ onBack, onFull, onComposition, joinCode = "", setJoinCode, onJoin, joined, onClasses }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const join = async () => {
    if (!joinCode.trim() || busy) return;
    setBusy(true); setErr("");
    try { await onJoin(joinCode); } catch (e) { setErr(detailText(e?.response?.data?.detail) || "No active course with that join code."); } finally { setBusy(false); }
  };
  return (
    <div data-testid="portal-pathway">
      {onBack ? <button data-testid="portal-back" onClick={onBack} className="text-[12px] text-stone-500 hover:text-[#8C3A2A] mb-4">← Back</button> : null}
      <h1 className="font-serif text-3xl text-stone-900">How would you like to work?</h1>
      {/* MY CLASSES — join a course with the teacher's code; the assignments the teacher set live there */}
      <div data-testid="portal-classes-card" className="mt-8 rounded-2xl border-2 border-stone-200 bg-white p-6">
        <div className="flex flex-wrap items-start gap-5">
          <div className="min-w-[220px] flex-1">
            <Users className="h-6 w-6 text-[#8C3A2A]" />
            <h2 className="font-serif text-xl text-stone-900 mt-3">My classes</h2>
            <p className="text-[13.5px] text-stone-600 mt-1.5">Join a course with the code your teacher shared, and work on the assignments they set.</p>
            {joined ? <p data-testid="portal-joined" className="mt-2 text-[13px] text-emerald-700">Joined <span className="font-medium">{joined.title}</span>.</p> : null}
            <button data-testid="portal-classes-open" onClick={onClasses} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#8C3A2A] mt-3 hover:underline">Go to my classes <ArrowRight className="h-4 w-4" /></button>
          </div>
          <div className="w-full sm:w-auto">
            <label htmlFor="portal-join-code" className="block text-[11px] font-mono uppercase tracking-wider text-stone-500">Join code</label>
            <div className="mt-1 flex gap-2">
              <input id="portal-join-code" data-testid="portal-join-code" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="e.g. A24SQB" maxLength={12}
                onKeyDown={(e) => { if (e.key === "Enter") join(); }}
                className="w-36 font-mono uppercase text-[15px] px-3 py-2 rounded-md border border-stone-300 outline-none focus:border-[#8C3A2A]" />
              <button data-testid="portal-join-submit" onClick={join} disabled={busy || !joinCode.trim()}
                className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[14px] font-medium px-4 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-40">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join"}</button>
            </div>
            {err ? <p data-testid="portal-join-error" className="mt-1.5 text-[12.5px] text-red-600">{err}</p> : null}
          </div>
        </div>
      </div>
      <p className="mt-8 text-[13px] font-mono uppercase tracking-wider text-stone-500">Or write on your own</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-3">
        <button data-testid="portal-pathway-full" onClick={onFull}
          className="text-left rounded-2xl border-2 border-stone-200 bg-white p-6 hover:border-[#8C3A2A]/50 transition-colors group">
          <BookOpen className="h-6 w-6 text-[#8C3A2A]" />
          <h2 className="font-serif text-xl text-stone-900 mt-3">Full Writing Studio</h2>
          <p className="text-[13.5px] text-stone-600 mt-1.5">Develop your ideas and your writing from beginning to end.</p>
          <p data-testid="portal-pathway-full-desc" className="text-[13px] text-stone-600 mt-3 leading-relaxed">The Full Writing Studio will help you organize your essay from start to finish. It will help you organize and structure your <em>ideas</em> before you compose and perfect your essay.</p>
          <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#8C3A2A] mt-4">Start <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></span>
        </button>
        <button data-testid="portal-pathway-composition" onClick={onComposition}
          className="text-left rounded-2xl border-2 border-stone-200 bg-white p-6 hover:border-[#8C3A2A]/50 transition-colors group">
          <PenLine className="h-6 w-6 text-[#8C3A2A]" />
          <h2 className="font-serif text-xl text-stone-900 mt-3">Strengthen My Writing</h2>
          <p className="text-[13.5px] text-stone-600 mt-1.5">Already have something written? Type in anything from a paragraph to a full paper and make it stronger.</p>
          <p data-testid="portal-pathway-composition-desc" className="text-[13px] text-stone-600 mt-3 leading-relaxed">Strengthen My Writing is for writing that already exists — a paragraph, a short response, a partial draft or a full paper. TWS reads what it is doing, shows you what is working and what needs attention, and helps you strengthen it without taking over the writing. Use it when you substantially know what you are saying.</p>
          <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#8C3A2A] mt-4">Start <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></span>
        </button>
      </div>
      <p className="mt-3 text-[12.5px] text-stone-500">New to Strengthen My Writing? <a data-testid="portal-strengthen-tour" href="/how-strengthen-works" className="underline decoration-stone-300 underline-offset-4 hover:text-[#8C3A2A]">See how it works — 2 min</a></p>
    </div>
  );
}

const detailText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg || "").filter(Boolean).join(" ") : "");

// SIGN IN AS — one account, several contexts. Student / Individual writer are self-service (added to an existing account on request);
// Teacher / Administrator are held by invitation only and sign-in can never grant them.
export const SIGN_IN_AS = [
  { id: "student", label: "Student", hint: "Join a course, or write on your own" },
  { id: "teacher", label: "Teacher", hint: "Courses, assignments, student work" },
  { id: "admin", label: "Administrator", hint: "Dashboard and invitations" },
  { id: "user", label: "Individual writer", hint: "Write on your own, no course" },
];
const SELF_SERVICE = ["student", "user"];

function RoleChooser({ value, onChange, options, testid = "portal-signin-as" }) {
  return (
    <div data-testid={testid} role="radiogroup" aria-label="Sign in as" className="grid grid-cols-2 gap-2">
      {options.map((o) => {
        const on = value === o.id;
        return (
          <button key={o.id} type="button" role="radio" aria-checked={on} data-testid={`${testid}-${o.id}`} onClick={() => onChange(o.id)}
            className={`text-left rounded-md border px-3 py-2 transition-colors ${on ? "border-[#8C3A2A] bg-[#8C3A2A]/5" : "border-stone-300 bg-white hover:border-[#8C3A2A]/50"}`}>
            <span className={`block text-[14px] font-medium ${on ? "text-[#8C3A2A]" : "text-stone-800"}`}>{o.label}</span>
            <span className="block text-[11.5px] text-stone-500">{o.hint}</span>
          </button>
        );
      })}
    </div>
  );
}

function AuthPanel({ startMode = "login", onAuthed, onBack, joinCode = "" }) {
  const [mode, setMode] = useState(startMode);                                 // login | register | forgot
  const [as, setAs] = useState("student");                                     // the context chosen for this sign-in
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  const submit = useCallback(async () => {
    if (busy || !email.trim() || (mode !== "forgot" && !password)) return;
    setBusy(true); setErr("");
    try {
      if (mode === "forgot") { await forgotPassword(email.trim()); setSent(true); return; }
      const chosen = mode === "register" ? (SELF_SERVICE.includes(as) ? as : "student") : as;
      const data = mode === "login"
        ? await loginAccount({ email: email.trim(), password, as_role: chosen })                       // teacher/admin refused unless held
        : await registerAccount({ email: email.trim(), password, name: name.trim(), role: chosen });  // an existing account signs in + gains student access
      localStorage.setItem(AUTH_TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      onAuthed(data.user, mode, chosen);
    } catch (e) {
      setErr(detailText(e?.response?.data?.detail) || "That didn't work. Check your details and try again.");
    } finally { setBusy(false); }
  }, [busy, email, password, name, mode, as, onAuthed]);

  if (mode === "forgot") {
    return (
      <div data-testid="portal-forgot" className="max-w-md">
        <button data-testid="portal-back" onClick={() => { setMode("login"); setErr(""); setSent(false); }} className="text-[12px] text-stone-500 hover:text-[#8C3A2A] mb-4">← Back to sign in</button>
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-2">The Writing Studio</p>
        <h1 className="font-serif text-3xl text-stone-900">Reset your password</h1>
        {sent ? (
          <p data-testid="portal-forgot-sent" className="text-[14px] text-stone-600 mt-4">If an account exists for <span className="font-medium">{email.trim()}</span>, a reset link is on its way. It expires in one hour.</p>
        ) : (
          <div className="mt-6 space-y-3">
            <p className="text-[13.5px] text-stone-500">Enter your email and we'll send you a link to choose a new password.</p>
            <input data-testid="portal-forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              className="w-full text-[15px] px-3.5 py-2.5 rounded-md border border-stone-300 outline-none focus:border-[#8C3A2A]" />
            {err && <p data-testid="portal-auth-error" className="text-[13px] text-red-600">{err}</p>}
            <button data-testid="portal-forgot-submit" onClick={submit} disabled={busy || !email.trim()}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#8C3A2A] text-white text-[15px] font-medium px-5 py-3 rounded-md hover:bg-[#6B2C20] disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send reset link <ArrowRight className="h-4 w-4" /></>}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="portal-auth" className="max-w-md">
      <button data-testid="portal-back" onClick={onBack} className="text-[12px] text-stone-500 hover:text-[#8C3A2A] mb-4">← Back</button>
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-2">The Writing Studio</p>
      <h1 className="font-serif text-3xl text-stone-900">{mode === "login" ? "Sign in" : as === "user" ? "Create your account" : "Create your student account"}</h1>
      <p className="text-[13.5px] text-stone-500 mt-2">{mode === "login" ? "One account for everyone. Choose how you're using The Writing Studio today." : "Already have a Writing Studio account as a teacher or administrator? Use the same email and password — student access is added to your account. Teachers join by invitation."}</p>
      <div className="mt-5">
        <p className="text-[11px] font-mono uppercase tracking-wider text-stone-500 mb-2">{mode === "login" ? "Sign in as" : "I am a"}</p>
        <RoleChooser value={SELF_SERVICE.includes(as) || mode === "login" ? as : "student"} onChange={setAs}
          options={mode === "login" ? SIGN_IN_AS : SIGN_IN_AS.filter((o) => SELF_SERVICE.includes(o.id))} />
      </div>
      {joinCode ? <p data-testid="portal-join-note" className="mt-3 text-[13px] text-stone-600 bg-white border border-stone-200 rounded-md px-3 py-2">Your course join code <span className="font-mono text-[#8C3A2A]">{joinCode}</span> will be entered for you after you sign in.</p> : null}
      <div className="mt-6 space-y-3">
        {mode === "register" && (
          <input data-testid="portal-auth-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
            className="w-full text-[15px] px-3.5 py-2.5 rounded-md border border-stone-300 outline-none focus:border-[#8C3A2A]" />
        )}
        <input data-testid="portal-auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          className="w-full text-[15px] px-3.5 py-2.5 rounded-md border border-stone-300 outline-none focus:border-[#8C3A2A]" />
        <div className="relative">
          <input data-testid="portal-auth-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            className="w-full text-[15px] px-3.5 py-2.5 pr-11 rounded-md border border-stone-300 outline-none focus:border-[#8C3A2A]" />
          <button type="button" data-testid="portal-auth-show-password" aria-label={showPw ? "Hide password" : "Show password"} onClick={() => setShowPw((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-[#8C3A2A]">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
        </div>
        {err && <p data-testid="portal-auth-error" className="text-[13px] text-red-600">{err}</p>}
        <button data-testid="portal-auth-submit" onClick={submit} disabled={busy || !email.trim() || !password}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#8C3A2A] text-white text-[15px] font-medium px-5 py-3 rounded-md hover:bg-[#6B2C20] disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{mode === "login" ? `Sign in as ${(SIGN_IN_AS.find((o) => o.id === as) || SIGN_IN_AS[0]).label.toLowerCase()}` : "Create account"} <ArrowRight className="h-4 w-4" /></>}
        </button>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button data-testid="portal-auth-toggle" onClick={() => { setMode((m) => (m === "login" ? "register" : "login")); setErr(""); }}
            className="text-[13px] text-stone-500 hover:text-[#8C3A2A]">
            {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
          </button>
          <button data-testid="portal-auth-forgot" onClick={() => { setMode("forgot"); setErr(""); }} className="text-[13px] text-stone-500 hover:text-[#8C3A2A]">Forgot password?</button>
        </div>
        <GoogleSignInButton asRole={SELF_SERVICE.includes(as) ? as : ""} onError={setErr}
          onAuthed={(data) => { localStorage.setItem(AUTH_TOKEN_KEY, data.token); localStorage.setItem(USER_KEY, JSON.stringify(data.user)); onAuthed(data.user, "login", SELF_SERVICE.includes(as) ? as : "student"); }} />
      </div>
    </div>
  );
}
